import { Inject, Injectable } from "@angular/core";
import { AxiosApiClient, TenantDataApi } from "@smallstack/axios-api-client";
import { PageableCrudStore } from "@smallstack/store";
import { F_CUSTOM, SmallstackTypeTag, SQBuilder } from "@smallstack/typesystem";
import { BehaviorSubject } from "rxjs";
import { DataStore } from "./data.store";
import { FrontendTypeRegistry } from "./frontend-type.registry";
import { StoreRegistry } from "./store.registry";
import { TRANSLATION_HELPER } from "./translation-helper.service";
import * as i0 from "@angular/core";
import * as i1 from "@smallstack/axios-api-client";
import * as i2 from "./store.registry";
import * as i3 from "./data.store";
import * as i4 from "./frontend-type.registry";
export class DataTypeStore extends PageableCrudStore {
    constructor(axiosApiClient, storeRegistry, dataStore, frontendTypeRegistry, translationHelper) {
        super();
        this.axiosApiClient = axiosApiClient;
        this.translationHelper = translationHelper;
        this.customDataTypesInStoreRegistry$ = new BehaviorSubject(false);
        this.customDataTypesInTypeRegistry$ = new BehaviorSubject(false);
        // fill storeRegistry with custom stores
        void this.value$.subscribe((dataTypes) => {
            const currentCustomStores = Object.keys(storeRegistry.getAllStores()).filter((storeName) => storeName.startsWith("custom_"));
            const dataTypeNames = dataTypes ? dataTypes.map((dt) => dt.path) : [];
            // remove obsolete ones
            for (const ccs of currentCustomStores)
                if (!dataTypeNames.includes(ccs))
                    storeRegistry.removeStore(ccs);
            // add/replace stores
            for (const dataTypeName of dataTypeNames) {
                storeRegistry.registerStore("custom_" + dataTypeName, dataStore.for("custom", dataTypeName));
            }
            this.customDataTypesInStoreRegistry$.next(true);
        });
        // fill Type Registry with custom types
        void this.value$.subscribe((dataTypes) => {
            // remove custom types
            frontendTypeRegistry.removeTypesStartingWith("custom_");
            // add custom types
            if (dataTypes instanceof Array) {
                for (const dataType of dataTypes) {
                    const typeName = "custom_" + dataType.path;
                    frontendTypeRegistry.addType({
                        name: typeName,
                        icon: dataType.configuration?.icon ? dataType.configuration.icon : "gear",
                        toStringRepresentation: dataType.configuration?.toStringRepresentation,
                        url: dataType.configuration?.url,
                        permissions: {
                            create: dataType.httpMethodPermissions.postPermission,
                            delete: dataType.httpMethodPermissions.deletePermission,
                            read: dataType.httpMethodPermissions.getPermission,
                            readAll: dataType.httpMethodPermissions.getAllPermission,
                            update: dataType.httpMethodPermissions.putPermission
                        },
                        searchableFields: this.getSearchableFields(dataType),
                        getCount: async (searchQuery) => {
                            return this.axiosApiClient
                                .get(TenantDataApi)
                                .getDataCount({ feature: F_CUSTOM, type: dataType.path, search: SQBuilder.toBase64String(searchQuery) })
                                .then((res) => res.data);
                        },
                        tags: [SmallstackTypeTag.COMMON, SmallstackTypeTag.LINK_FOR_CALENDAR, SmallstackTypeTag.LINK_FOR_TODO]
                    });
                    // add translations
                    translationHelper.addJsonTranslations(["de_de"], {
                        types: {
                            [frontendTypeRegistry.tidyTypeName(typeName)]: {
                                singular: [dataType.title],
                                plural: [dataType.title]
                            }
                        }
                    });
                }
            }
            this.customDataTypesInTypeRegistry$.next(true);
        });
    }
    async loadModels(query) {
        const res = await this.axiosApiClient.get(TenantDataApi).getDataTypes(query);
        return res.data;
    }
    async loadModelById(id) {
        const res = await this.axiosApiClient.get(TenantDataApi).getDataType({ id });
        return res.data;
    }
    async deleteModelById(id) {
        const res = await this.axiosApiClient.get(TenantDataApi).deleteDataType({ id });
        return res.data;
    }
    async createModel(dataType) {
        const res = await this.axiosApiClient.get(TenantDataApi).createDataType({ dataType });
        return res.data;
    }
    async patchModel(id, dataType) {
        const res = await this.axiosApiClient.get(TenantDataApi).patchDataType({ id, dataType });
        return res.data;
    }
    async putModel(dataType) {
        const res = await this.axiosApiClient.get(TenantDataApi).putDataType({ dataType, id: dataType.id });
        return res.data;
    }
    async deleteModelsByIds(ids) {
        const res = await this.axiosApiClient.get(TenantDataApi).deleteManyDataTypes({ ids });
        return res.data;
    }
    getSearchableFields(dataType) {
        if (dataType.schema?.properties)
            return this.getSearchableFieldsForProperties(dataType.schema.properties);
        return [];
    }
    getSearchableFieldsForProperties(props) {
        const searchables = [];
        for (const prop in props) {
            if (props[prop]["x-type-info"]?.searchable !== false) {
                const weight = props[prop]["x-type-info"]?.searchableWeight ? props[prop]["x-type-info"]?.searchableWeight : 10;
                searchables.push({ name: prop, weight });
            }
            if (props[prop].properties) {
                const subs = this.getSearchableFieldsForProperties(props[prop].properties);
                if (subs.length > 0)
                    // prefix with parent prop
                    searchables.push(...subs.map((s) => {
                        s.name = prop + "." + s.name;
                        return s;
                    }));
            }
        }
        return searchables;
    }
}
DataTypeStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: DataTypeStore, deps: [{ token: i1.AxiosApiClient }, { token: i2.StoreRegistry }, { token: i3.DataStore }, { token: i4.FrontendTypeRegistry }, { token: TRANSLATION_HELPER }], target: i0.ɵɵFactoryTarget.Injectable });
DataTypeStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: DataTypeStore });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: DataTypeStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.AxiosApiClient }, { type: i2.StoreRegistry }, { type: i3.DataStore }, { type: i4.FrontendTypeRegistry }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLATION_HELPER]
                }] }]; } });
